import { Alert, Card, Checkbox, Col, ConfigProvider, DatePicker, Divider, Form, Input, Radio, Row, Statistic, Typography } from "antd";
import SDIModal from "../../../../components/ui/SDIModal";
import PropTypes from "prop-types";
import frFR from "antd/locale/fr_FR";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../../../../variables/api/axiosInstance";
import { displayNumbers } from "../../../../variables/numbersFunctions";

const { Text, Title } = Typography;
const { TextArea } = Input;

const Resiliation = (props) => {
    const [modalResiliation, setModalResiliation] = useState(props.open);
    const [loading, setLoading] = useState(false);
    const [form] = Form.useForm();

    // États pour les données du contrat et de la résiliation
    const [contratActuel, setContratActuel] = useState(null);
    const [dateDemandeResiliation, setDateDemandeResiliation] = useState(dayjs());
    const [dateEffetResiliation, setDateEffetResiliation] = useState(null);
    const [dureeRestante, setDureeRestante] = useState(0);
    const [joursNonConsommes, setJoursNonConsommes] = useState(0);
    const [dureeeTotaleContrat, setDureeTotaleContrat] = useState(0);
    const [preview, setPreview] = useState(null);
    const [modeRistourne, setModeRistourne] = useState('REMBOURSEMENT'); // REMBOURSEMENT ou COMPENSATION
    const [motifResiliation, setMotifResiliation] = useState('');

    // Confirmations requises
    const [conditionsSignees, setConditionsSignees] = useState(false);
    const [documentsRendus, setDocumentsRendus] = useState(false);

    const DELAI_PREAVIS_JOURS = 60; // 2 mois = 60 jours
    const DUREE_MINIMALE_MOIS = 3; // 3 mois minimum

    useEffect(() => {
        setModalResiliation(props.open);
        if (props.open && props?.contrat?.codeId) {
            console.log("Contrat pour résiliation:", props?.contrat);
            setContratActuel(props?.contrat);

            // Calculer la date d'effet (demande + préavis)
            const dateEffet = dayjs().add(DELAI_PREAVIS_JOURS, 'day');
            setDateEffetResiliation(dateEffet);

            // Calculer la durée totale du contrat
            const dateDebut = dayjs(props?.contrat?.dateEffet);
            const dateEcheance = dayjs(props?.contrat?.dateEcheance);
            const dureeTotale = dateEcheance.diff(dateDebut, 'day');
            setDureeTotaleContrat(dureeTotale);

            // Calculer la durée restante
            const dureeRestanteMois = dateEcheance.diff(dateEffet, 'month', true);
            setDureeRestante(dureeRestanteMois);

            // Calculer les jours non consommés
            const joursNonConso = dateEcheance.diff(dateEffet, 'day');
            setJoursNonConsommes(joursNonConso);
        }
    }, [props.open, props?.contrat?.codeId]);

    const { CalendarOutlined, DollarOutlined, InfoCircleOutlined, CheckCircleOutlined, WarningOutlined, ClockCircleOutlined } = require('@ant-design/icons');

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XAF' }).format(value);
    };

    const handleDateDemandeChange = (date) => {
        setDateDemandeResiliation(date);

        // Recalculer la date d'effet (demande + préavis de 2 mois)
        const nouvelleDateEffet = date.add(DELAI_PREAVIS_JOURS, 'day');
        setDateEffetResiliation(nouvelleDateEffet);

        // Recalculer les jours non consommés et la durée restante
        if (contratActuel?.dateEcheance) {
            const dateEcheance = dayjs(contratActuel.dateEcheance);
            const joursNonConso = dateEcheance.diff(nouvelleDateEffet, 'day');
            setJoursNonConsommes(joursNonConso);

            const dureeRestanteMois = dateEcheance.diff(nouvelleDateEffet, 'month', true);
            setDureeRestante(dureeRestanteMois);
        }
    };

    const contratEffetDansLeFutur = useMemo(() => {
        if (!contratActuel?.dateEffet) {
            return false;
        }
        return dayjs(contratActuel.dateEffet).isAfter(dayjs(), 'day');
    }, [contratActuel?.dateEffet]);

    const calculatePreview = async () => {
        if (!contratActuel || !dateEffetResiliation) return;

        // Bloquer le calcul si contrat futur
        if (contratEffetDansLeFutur) {
            setPreview(null);
            return;
        }

        try {
            const payload = {
                contratCodeId: contratActuel.codeId,
                dateEffet: dateEffetResiliation.format('YYYY-MM-DDTHH:mm:ss'),
                joursNonConsommes,
                modeRistourne
            };

            const response = await axiosInstance.put('/api/contrat/preview-resiliation', payload);
            console.log("Preview résiliation:", response.data);
            setPreview(response.data);
        } catch (error) {
            console.error("Erreur lors du calcul du preview:", error);
            
            // Gestion améliorée des erreurs 400
            if (error?.response?.status === 400) {
                const backendMessage = error?.response?.data?.message || 
                                      error?.response?.data?.error ||
                                      "La résiliation n'est pas autorisée pour ce contrat";
                toast.error(backendMessage, {
                    autoClose: 5000,
                    position: "top-right"
                });
            } else if (error?.response?.status === 404) {
                toast.error("Contrat introuvable");
            } else if (error?.response?.status === 500) {
                toast.error("Erreur serveur lors du calcul de la résiliation");
            } else {
                toast.error("Erreur lors du calcul du prévisionnel de résiliation");
            }
            setPreview(null);
        }
    };

    useEffect(() => {
        // Ne calculer le preview que si le contrat n'est pas futur
        if (dateEffetResiliation && joursNonConsommes > 0 && !contratEffetDansLeFutur) {
            calculatePreview();
        } else if (contratEffetDansLeFutur) {
            setPreview(null);
        }
    }, [dateEffetResiliation, joursNonConsommes, modeRistourne, contratActuel, contratEffetDansLeFutur]);

    const navigate = useNavigate();

    const enterLoading = () => setLoading(true);
    const outLoading = () => setLoading(false);

    const handleResiliationSubmit = async () => {
        try {
            await form.validateFields();

            // Vérification contrat futur - message plus explicite
            if (contratEffetDansLeFutur) {
                toast.warning("Action impossible : La résiliation n'est pas autorisée pour un contrat dont la date d'effet est future. Veuillez procéder à une annulation.", {
                    autoClose: 6000
                });
                return;
            }

            // Vérifications d'éligibilité
            if (!isContratEligible()) {
                toast.error(getMessageNonEligibilite(), {
                    autoClose: 5000
                });
                return;
            }

            if (!documentsRendus) {
                toast.error("Vous devez confirmer que les documents ont été rendus");
                return;
            }

            enterLoading();

            const payload = {
                contratCodeId: contratActuel.codeId,
                dateEffet: dateEffetResiliation.format('YYYY-MM-DDTHH:mm:ss'),
                primeNette: preview?.primeNette || 0,
                ristourne: modeRistourne === "COMPENSATION" ? false : true,
                taxes: preview?.taxes,
                primeTotale: preview?.primeNetteFractionnee,
                accessoires: preview?.accessoires
            };

            console.log("Payload résiliation:", payload);

            const response = await axiosInstance.put(
                `/api/contrat/resiliation`,
                payload
            );

            toast.success("Résiliation du contrat effectuée avec succès!", {
                autoClose: 3000
            });
            
            setModalResiliation(false);
            props.update(false);
            navigate(`/details-contrat/${response.data.codeId}`, { replace: true });
            window.location.reload();

        } catch (error) {
            console.error("Erreur lors de la résiliation:", error);
            
            // Gestion détaillée des erreurs
            if (error?.response?.status === 400) {
                const backendMessage = error?.response?.data?.message || 
                                      error?.response?.data?.error ||
                                      "La résiliation n'est pas autorisée pour ce contrat";
                
                // Message spécifique pour contrat futur
                if (backendMessage.toLowerCase().includes('futur') || 
                    backendMessage.toLowerCase().includes('effet')) {
                    toast.warning(`${backendMessage}. Merci d'utiliser l'annulation.`, {
                        autoClose: 6000
                    });
                } else {
                    toast.error(backendMessage, {
                        autoClose: 5000
                    });
                }
            } else if (error?.response?.status === 404) {
                toast.error("Contrat introuvable");
            } else if (error?.response?.status === 403) {
                toast.error("Vous n'avez pas les droits pour effectuer cette action");
            } else if (error?.response?.status === 500) {
                toast.error("Erreur serveur lors de la résiliation du contrat");
            } else if (error?.code === 'ECONNABORTED' || error?.message?.includes('timeout')) {
                toast.error("La requête a pris trop de temps. Veuillez réessayer.");
            } else {
                toast.error("Erreur lors de la résiliation du contrat");
            }
        } finally {
            outLoading();
        }
    };

    const handleCancel = () => {
        setModalResiliation(false);
        props.update(false);
    };

    const isContratEligible = () => {
        // Bloquer si contrat futur
        if (contratEffetDansLeFutur) {
            return false;
        }

        const estEnCoursValidite =
            !contratActuel?.resilie &&
            !contratActuel?.suspendu;
        const nonSinistre = !contratActuel?.sinistralite;
        const dureeeSuffisante = dureeRestante >= DUREE_MINIMALE_MOIS;
        const respecteTroisDouzieme = joursNonConsommes > (dureeeTotaleContrat * 3 / 12);

        return estEnCoursValidite && nonSinistre && dureeeSuffisante && respecteTroisDouzieme;
    };

    const getMessageNonEligibilite = () => {
        if (contratEffetDansLeFutur) {
            return "La date d'effet du contrat est future. Seule une annulation est autorisée.";
        }
        if (contratActuel?.resilie || contratActuel?.avenant?.code === 'RS') {
            return "Le contrat doit être en cours de validité.";
        }
        if (contratActuel?.suspendu || contratActuel?.avenant?.code === 'SP') {
            return "Un contrat suspendu ne peut pas être résilié.";
        }
        if (contratActuel?.sinistralite) {
            return "Un contrat sinistré ne peut pas être résilié.";
        }
        if (dureeRestante < DUREE_MINIMALE_MOIS) {
            return `La durée restante doit être supérieure à ${DUREE_MINIMALE_MOIS} mois.`;
        }
        if (joursNonConsommes <= (dureeeTotaleContrat * 3 / 12)) {
            return "La durée restante doit être supérieure à 3/12 de la durée totale du contrat.";
        }
        return "Le contrat ne remplit pas les conditions de résiliation.";
    };

    return (
        <SDIModal
            title="RÉSILIATION DE CONTRAT"
            centered
            confirmLoading={loading}
            open={modalResiliation}
            onOk={handleResiliationSubmit}
            onCancel={handleCancel}
            okButtonProps={{
                disabled: contratEffetDansLeFutur || !isContratEligible() || !preview,
                danger: false,
                title: contratEffetDansLeFutur 
                    ? "Impossible : contrat à date d'effet future" 
                    : !isContratEligible()
                    ? "Contrat non éligible"
                    : !preview
                    ? "Calcul en cours..."
                    : "Cliquez pour confirmer la résiliation"
            }}
            width={1000}
            zIndex={1940}
            okText={contratEffetDansLeFutur ? "Résiliation impossible" : "Résilier le contrat"}
            cancelText="Annuler"
        >
            <ConfigProvider locale={frFR}>
                <div className="resiliation-content">

                    {contratEffetDansLeFutur && (
                        <Alert
                            message="⚠️ Contrat non encore en vigueur"
                            description={
                                <div>
                                    <p style={{ marginBottom: '8px' }}>
                                        <strong>La résiliation n'est pas autorisée</strong> tant que la date d'effet du contrat n'est pas atteinte. Veuillez effectuer une <strong>annulation</strong> plutôt qu'une résiliation.
                                    </p>
                                    <p style={{ marginBottom: '0' }}>
                                        📌 <strong>Action recommandée :</strong> Procédez à une <strong>annulation</strong> du contrat si nécessaire.
                                    </p>
                                    <p style={{ marginTop: '8px', marginBottom: '0', fontSize: '12px', color: '#8c8c8c' }}>
                                        Date d'effet du contrat : {dayjs(contratActuel.dateEffet).format('DD/MM/YYYY')}
                                    </p>
                                </div>
                            }
                            type="warning"
                            showIcon
                            banner
                            className="mb-2"
                            style={{ 
                                borderLeft: '4px solid #faad14',
                                backgroundColor: '#fffbe6'
                            }}
                        />
                    )}

                    {/* Informations sur le contrat */}
                    {contratActuel && (
                        <Card className="mb-2" size="small" title="Contrat à résilier">
                            <Row gutter={16}>
                                <Col span={8}>
                                    <Statistic
                                        title="Numéro de police"
                                        value={contratActuel.numeroPoliceP || contratActuel.numeroPoliceM}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Statistic
                                        title="Durée restante"
                                        value={`${Math.floor(dureeRestante)} mois`}
                                        valueStyle={{ color: dureeRestante >= DUREE_MINIMALE_MOIS ? '#3f8600' : '#cf1322' }}
                                    />
                                </Col>
                                <Col span={8}>
                                    <Statistic
                                        title="Jours non consommés"
                                        value={`${joursNonConsommes} jours`}
                                        valueStyle={{ color: '#1890ff' }}
                                    />
                                </Col>
                            </Row>
                        </Card>
                    )}

                    {/* Conditions d'éligibilité */}
                    {!isContratEligible() ? (
                        <Alert
                            message="Contrat non éligible à la résiliation"
                            description={getMessageNonEligibilite()}
                            type="error"
                            icon={<WarningOutlined />}
                            showIcon
                            className="mb-2"
                        />
                    ) : null}

                    <Form form={form} layout="vertical">
                        {/* Date de demande */}
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    label="Date de demande de résiliation"
                                    name="dateDemandeResiliation"
                                    rules={[{ required: true, message: 'Veuillez sélectionner une date' }]}
                                    initialValue={dateDemandeResiliation}
                                >
                                    <DatePicker
                                        style={{ width: '100%' }}
                                        format="DD/MM/YYYY"
                                        value={dateDemandeResiliation}
                                        onChange={handleDateDemandeChange}
                                        disabledDate={(current) => {
                                            const dateEcheance = contratActuel ? dayjs(contratActuel.dateEcheance) : dayjs().add(1, 'year');
                                            // Date ne peut pas être dans le futur ou après échéance - préavis
                                            return current && (
                                                current > dayjs().endOf('day') ||
                                                current.add(DELAI_PREAVIS_JOURS, 'day') > dateEcheance
                                            );
                                        }}
                                    />
                                </Form.Item>
                            </Col>

                            <Col span={12}>
                                <Form.Item label="Date d'effet de la résiliation (après préavis)">
                                    <Input
                                        value={dateEffetResiliation ? dateEffetResiliation.format('DD/MM/YYYY') : ''}
                                        disabled
                                        style={{ backgroundColor: '#f0f8ff', fontWeight: 'bold' }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        {/* Informations calculées */}
                        {!contratEffetDansLeFutur && (
                            <Card className="mb-2" size="small" title="Informations sur le préavis">
                                <Row gutter={16}>
                                    <Col span={8}>
                                        <Statistic
                                            title="Délai de préavis"
                                            value={`${DELAI_PREAVIS_JOURS} jours`}
                                            prefix={<ClockCircleOutlined />}
                                        />
                                    </Col>
                                    <Col span={8}>
                                        <Statistic
                                            title="Date d'échéance originale"
                                            value={contratActuel ? dayjs(contratActuel.dateEcheance).format('DD/MM/YYYY') : 'N/A'}
                                            prefix={<CalendarOutlined />}
                                        />
                                    </Col>
                                    <Col span={8}>
                                        <Statistic
                                            title="Pourcentage de ristourne"
                                            value="75%"
                                            valueStyle={{ color: '#52c41a' }}
                                        />
                                    </Col>
                                </Row>
                            </Card>
                        )}

                        {/* Mode de ristourne */}
                        {!contratEffetDansLeFutur && (
                            <Form.Item
                                label="Mode de traitement de la ristourne"
                                name="modeRistourne"
                                rules={[{ required: true, message: 'Veuillez sélectionner un mode' }]}
                                initialValue={modeRistourne}
                            >
                                <Radio.Group
                                    value={modeRistourne}
                                    onChange={(e) => setModeRistourne(e.target.value)}
                                    className="mode-ristourne-group row"
                                >
                                    <div className="col-6">
                                        <Radio value="REMBOURSEMENT">
                                            <div className="option-content">
                                                <Title level={5} className="option-title">
                                                    Remboursement
                                                </Title>
                                                <Text type="secondary" className="option-description">
                                                    La ristourne sera remboursée par la société d'assurance
                                                </Text>
                                            </div>
                                        </Radio>
                                    </div>

                                    <div className="col-6">
                                        <Radio value="COMPENSATION">
                                            <div className="option-content">
                                                <Title level={5} className="option-title">
                                                    Compensation
                                                </Title>
                                                <Text type="secondary" className="option-description">
                                                    La ristourne sera utilisée pour compenser une prime due
                                                </Text>
                                            </div>
                                        </Radio>
                                    </div>
                                </Radio.Group>
                            </Form.Item>
                        )}

                        {/* Confirmations obligatoires */}
                        {!contratEffetDansLeFutur && (
                            <Card className="mb-2" size="small" title="Vérifications obligatoires du producteur">
                                <Form.Item
                                    name="documentsRendus"
                                    valuePropName="checked"
                                    rules={[
                                        {
                                            validator: (_, value) =>
                                                value ? Promise.resolve() : Promise.reject(new Error('Confirmation requise'))
                                        }
                                    ]}
                                >
                                    <Checkbox
                                        checked={documentsRendus}
                                        onChange={(e) => setDocumentsRendus(e.target.checked)}
                                    >
                                        <Text strong>
                                            Attestations et vignettes récupérées de l'assuré
                                        </Text>
                                    </Checkbox>
                                </Form.Item>
                            </Card>
                        )}

                        {/* Récapitulatif financier */}
                        {preview && !contratEffetDansLeFutur && (
                            <Card className="cost-summary" size="small">
                                <Title level={5}>
                                    <DollarOutlined className="mr-2" />
                                    Récapitulatif financier
                                </Title>
                                <div className="cost-breakdown">
                                    <Row justify="space-between" className="cost-line">
                                        <Col>
                                            <Text>Prime totale du contrat:</Text>
                                        </Col>
                                        <Col>
                                            <Text>{formatCurrency(preview.primeNette || 0)}</Text>
                                        </Col>
                                    </Row>

                                    <Row justify="space-between" className="cost-line">
                                        <Col>
                                            <Text>Jours non consommés:</Text>
                                        </Col>
                                        <Col>
                                            <Text>{joursNonConsommes} jours</Text>
                                        </Col>
                                    </Row>

                                    <Row justify="space-between" className="cost-line">
                                        <Col>
                                            <Text>Prorata des jours non consommés:</Text>
                                        </Col>
                                        <Col>
                                            <Text>{formatCurrency(preview.primeProrata || 0)} {`(${new Intl.NumberFormat('fr-FR').format(preview.prorata || 0)})`}</Text>
                                        </Col>
                                    </Row>

                                    <Row justify="space-between" className="cost-line">
                                        <Col>
                                            <Text>Ristourne TTC (75% du prorata):</Text>
                                        </Col>
                                        <Col>
                                            <Text strong className="text-green-600">
                                                {formatCurrency(preview.primeNetteFractionnee || 0)}
                                            </Text>
                                        </Col>
                                    </Row>
                                    <Row justify="space-between" className="cost-line">
                                        <Col>
                                            <Text>Frais accessoires:</Text>
                                        </Col>
                                        <Col>
                                            <Text strong className="text-green-600">
                                                {formatCurrency(preview.accessoires || 0)}
                                            </Text>
                                        </Col>
                                    </Row>
                                    <Divider className="my-2" />

                                    <Row justify="space-between" className="cost-line total-cost">
                                        <Col>
                                            <Text strong className="text-lg">
                                                {modeRistourne === 'REMBOURSEMENT' ? 'MONTANT À REMBOURSER:' : 'MONTANT DE COMPENSATION:'}
                                            </Text>
                                        </Col>
                                        <Col>
                                            <Text strong className="text-lg text-green-600">
                                                {formatCurrency(preview.primeNetteFractionnee || 0)}
                                            </Text>
                                        </Col>
                                    </Row>
                                </div>
                            </Card>
                        )}
                    </Form>
                </div>
            </ConfigProvider>

            <style jsx>
                {`
                    .resiliation-content {
                        max-height: 70vh;
                        overflow-y: auto;
                    }
                    
                    .info-section {
                        display: flex;
                        flex-direction: column;
                        gap: 12px;
                    }
                    
                    .info-item {
                        display: flex;
                        align-items: flex-start;
                        gap: 8px;
                    }
                    
                    .info-icon {
                        margin-top: 2px;
                        font-size: 16px;
                    }
                    
                    .info-icon.warning {
                        color: #faad14;
                    }
                    
                    .info-icon.info {
                        color: #1890ff;
                    }
                    
                    .info-icon.success {
                        color: #52c41a;
                    }
                    
                    .mode-ristourne-group {
                        width: 100%;
                    }
                    
                    .option-item {
                        display: block;
                        margin-bottom: 12px;
                        padding: 12px;
                        border: 1px solid #d9d9d9;
                        border-radius: 6px;
                        transition: border-color 0.3s;
                    }
                    
                    .option-item:hover {
                        border-color: #1890ff;
                    }
                    
                    .option-content {
                        margin-left: 24px;
                    }
                    
                    .option-title {
                        margin: 0 0 4px 0 !important;
                        font-size: 14px !important;
                    }
                    
                    .option-description {
                        display: block;
                        font-size: 12px;
                        line-height: 1.4;
                    }
                    
                    .cost-summary {
                        background-color: #f6ffed;
                        border: 1px solid #b7eb8f;
                    }
                    
                    .cost-breakdown .cost-line {
                        margin-bottom: 8px;
                    }
                    
                    .cost-breakdown .total-cost {
                        background-color: #e6fffb;
                        padding: 8px;
                        border-radius: 4px;
                        margin-top: 8px;
                    }
                    
                    .mr-2 { margin-right: 8px; }
                    .mb-2 { margin-bottom: 16px; }
                    .mb-4 { margin-bottom: 16px; }
                    .my-2 { margin: 8px 0; }
                    .text-lg { font-size: 16px; }
                    .text-green-600 { color: #52c41a; }
                `}
            </style>
        </SDIModal>
    );
};

Resiliation.propTypes = {
    open: PropTypes.bool,
    update: PropTypes.func,
    contrat: PropTypes.object
};

export default Resiliation;
